<template>
    <b-modal
            ref="registrationModal"
            :id="id"
            title="Registration"
            :ok-only="submitSuccess">
        <template slot="modal-footer">
            <b-button variant="secondary" @click="$refs.registrationModal.hide()">
                CANCEL
            </b-button>
            <pb-btn :busy="submitProcessing || checkingEmail" variant="primary"
                            v-on:click.native="onSubmit">
                <span v-if="formData.newCustomer != null">
                    {{submitSuccess ? 'OK' : 'REGISTER'}}
                </span>
                <span v-else>
                    NEXT
                </span>
            </pb-btn>
        </template>
        <b-container class="text-left">
            <b-row>
                <b-col v-if="submitSuccess">
                    <p>You've successfully signed up. In order to verify and activate your account, please check your
                        inbox for further instructions.</p>
                </b-col>
                <b-col v-else>
                    <b-form @submit="onSubmit">
                        <b-row class="my-2" v-if="submitError">
                            <b-col>
                                <b-alert variant="danger" show>{{submitError}}</b-alert>
                            </b-col>
                        </b-row>
                        <b-row class="my-2">
                            <b-col>
                                <label for="email">Email:</label>
                                <b-form-input
                                        :disabled="formData.newCustomer != null"
                                        id="email"
                                        name="email"
                                        type="text"
                                        placeholder="Email"
                                        v-model.trim="formData.email"
                                        v-validate="'required|email'"
                                        class="secondary"
                                        :state="validateState('email')"
                                ></b-form-input>
                            </b-col>
                        </b-row>
                        <div v-if="formData.newCustomer != null">
                            <fieldset class="form-group">
                                <hr class="light w-100"/>
                                <b-row class="my-2">
                                    <b-col>
                                        <label for="firstName">First Name:</label>
                                        <b-form-input
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                placeholder="First Name"
                                                v-model.trim="formData.firstName"
                                                v-validate="'required'"
                                                class="secondary"
                                                :state="validateState('firstName')"
                                        ></b-form-input>
                                    </b-col>
                                    <b-col>
                                        <label for="lastName">Last Name:</label>
                                        <b-form-input
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                placeholder="Last Name"
                                                v-model.trim="formData.lastName"
                                                v-validate="'required'"
                                                class="secondary"
                                                :state="validateState('lastName')"
                                        ></b-form-input>
                                    </b-col>
                                </b-row>
                                <b-row class="my-2">
                                    <b-col>
                                        <label for="company">Company:</label>
                                        <b-form-input
                                                id="company"
                                                name="company"
                                                type="text"
                                                placeholder="Company"
                                                v-model.trim="formData.company"
                                                v-validate="'required'"
                                                class="secondary"
                                                :state="validateState('company')"
                                        ></b-form-input>
                                    </b-col>
                                </b-row>
                                <b-row class="my-2">
                                    <b-col>
                                        <label for="password">Password:</label>
                                        <b-form-input
                                                id="password"
                                                name="password"
                                                type="password"
                                                ref="password"
                                                placeholder="Password"
                                                v-model.trim="formData.password"
                                                v-validate="'required'"
                                                class="secondary"
                                                :state="validateState('password')"
                                        ></b-form-input>
                                    </b-col>
                                    <b-col>
                                        <label for="confirmPassword">Confirm Password:</label>
                                        <b-form-input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type="password"
                                                placeholder="Confirm Password"
                                                data-vv-as="password"
                                                v-model.trim="formData.confirmPassword"
                                                v-validate="'required|confirmed:password'"
                                                class="secondary"
                                                :state="validateState('confirmPassword')"
                                        ></b-form-input>
                                    </b-col>
                                </b-row>
                            </fieldset>
                        </div>
                        <fieldset v-if="formData.newCustomer === true" class="form-group">
                            <hr class="light w-100"/>
                            <b-row class="my-2">
                                <b-col>
                                    <label for="country">Country:</label>
                                    <v-select id="country"
                                              :clearable="false"
                                              name="country"
                                              placeholder="Please select..."
                                              v-validate="'required'"
                                              v-bind:class="[{'is-invalid': errors.has('country')}, 'secondary']"
                                              v-model="selectedCountry"
                                              @input="onSelectCountry"
                                              label="name"
                                              :options="countryList">
                                    </v-select>
                                </b-col>
                            </b-row>
                            <b-row class="my-2">
                                <b-col>
                                    <label for="street">Street:</label>
                                    <b-form-input
                                            id="street"
                                            name="street"
                                            type="text"
                                            placeholder="Street and House Nr."
                                            v-model.trim="formData.street"
                                            v-validate="'required'"
                                            class="secondary"
                                            :state="validateState('street')"
                                    ></b-form-input>
                                </b-col>
                            </b-row>
                            <b-row class="my-2">
                                <b-col cols="4">
                                    <label for="zipCode">Zip Code:</label>
                                    <b-form-input
                                            id="zipCode"
                                            name="zipCode"
                                            type="text"
                                            v-model.trim="formData.zipCode"
                                            v-validate="'required'"
                                            placeholder="Zip Code"
                                            class="secondary"
                                            :state="validateState('zipCode')"
                                    ></b-form-input>
                                </b-col>
                                <b-col cols="8">
                                    <label for="city">City:</label>
                                    <b-form-input
                                            id="city"
                                            name="city"
                                            type="text"
                                            v-model.trim="formData.city"
                                            v-validate="'required'"
                                            placeholder="City"
                                            class="secondary"
                                            :state="validateState('formData')"
                                    ></b-form-input>
                                </b-col>
                            </b-row>
                            <b-row class="my-2">
                                <b-col>
                                    <label for="phone">Phone
                                        <small>(Optional)</small>
                                    </label>
                                    <b-form-input
                                            id="phone"
                                            name="phone"
                                            type="text"
                                            placeholder="With country code (+49...)"
                                            v-model.trim="formData.phone"
                                            class="secondary"
                                            :state="validateState('phone')"
                                    ></b-form-input>
                                </b-col>
                            </b-row>
                            <b-row class="my-2">
                                <b-col>
                                    <label for="taxId">Tax ID
                                        <small>(Optional)</small>
                                    </label>
                                    <b-form-input
                                            id="taxId"
                                            name="taxId"
                                            type="text"
                                            placeholder="Tax ID"
                                            v-model.trim="formData.taxId"
                                            class="secondary"
                                            :state="validateState('taxId')"
                                    ></b-form-input>
                                </b-col>
                            </b-row>
                        </fieldset>
                    </b-form>
                </b-col>
            </b-row>
        </b-container>
    </b-modal>
</template>
<script>

    import LoadingButton from "../../components/LoadingButton";

    function initialData() {
        return {
            checkingEmail: false,
            submitSuccess: false,
            submitProcessing: false,
            submitError: null,
            selectedCountry: null,
            formData: {
                firstName: null,
                lastName: null,
                company: null,
                email: null,
                password: null,
                confirmPassword: null,
                countryId: null,
                street: null,
                zipCode: null,
                city: null,
                phone: null,
                taxId: null,
                newCustomer: null
            }
        }
    }

    import {mapActions} from "vuex";
    import UserApi from "@shared/_api/user";
    import ShoppingCartApi from "@shared/_api/shoppingCart";

    export default {
        name: 'user-bar',
        props: {
            id: String
        },
        data() {
            return {countryList: null, ...initialData()};
        },
        created() {
            this.fetchCountryOptions();
        },
        methods: {
            ...mapActions("account", ["register"]),
            onSelectCountry(country) {
                this.formData.countryId = country.id;
            },
            fetchCountryOptions() {
                ShoppingCartApi.getCountryList()
                    .then(countryList => {
                        this.countryList = countryList;
                    }).finally(() => {

                    this.submitProcessing = false;
                });
            },
            resetData() {
                this.$validator.reset().finally(() => {
                    Object.assign(this.$data, initialData());
                });
            },
            checkEmail() {
                this.submitError = null;
                this.checkingEmail = true;
                this.$validator.validate().then(valid => {
                    if (valid) {
                        return UserApi.isKnownEmail(this.formData.email).then(() =>
                            this.formData.newCustomer = false
                        ).catch((err) => {
                                if (Number(err.status) === 404) {
                                    this.formData.newCustomer = true
                                } else if (err.errorCode === 'DUPLICATE_USER_REGISTRATION') {
                                    this.submitError = "A user with the same E-Mail address does already exist.";
                                } else {
                                    this.submitError = "An error occured. Please contact our customer support.";
                                }
                            }
                        )
                    }
                    return Promise.resolve();
                }).finally(() => {
                    this.$validator.reset().finally(() => {
                        this.checkingEmail = false
                    });
                })
            },
            onSubmit(evt) {
                evt.preventDefault();
                if(this.formData.newCustomer == null) {
                    this.checkEmail();
                } else {
                    this.submitError = null;
                    if (this.submitSuccess) {
                        this.$nextTick(() => {
                            this.$emit('ok');
                            this.$refs.registrationModal.hide();
                        })
                    } else {
                        this.$validator.validate().then(valid => {
                            if (valid) {
                                this.submitProcessing = true;
                                this.formData.clientUrl = window.location.origin + "/activate/";
                                UserApi.register(this.formData).then(() => {
                                    this.submitSuccess = true;
                                }).catch(err => {
                                    if (err.errorCode === 'DUPLICATE_USER_REGISTRATION') {
                                        this.submitError = "A user with the same E-Mail address does already exist.";
                                    } else {
                                        this.submitError = "An error occured. Please contact our customer support.";
                                    }

                                }).finally(() => {
                                    this.submitProcessing = false;
                                });
                            }
                        });
                    }
                }
            },
            validateState(ref) {
                if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
                    return !this.errors.has(ref)
                }
                return null
            },
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>

</style>
